import { Component, Input } from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import { LucideIcon } from '@lucide/angular';
import { animate, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { IconComponent } from '../icon/icon.component';

export type MenuItem =
    {
        // string = legacy @ng-icons/heroicons name, LucideIcon = migrated (see icon-system.md).
        // Both are supported so callers can migrate one at a time.
        icon: string | LucideIcon;
        label: string;
        bold?: boolean;
    }
    & ({
    href: string | string[]
} | {
    execute: () => void
});

export interface GroupedMenuItems {
    group: string;
    items: MenuItem[];
}

@Component({
    selector: 'app-dropdown-menu',
    imports: [
        NgIcon,
        IconComponent,
    ],
    templateUrl: './dropdown-menu.component.html',
    styleUrl: './dropdown-menu.component.scss',
    animations: [
        trigger('backdropAnimation', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('150ms ease-in', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('150ms ease-out', style({ opacity: 0 })),
            ]),
        ]),
        trigger('menuAnimation', [
            transition(':enter', [
                style({
                    opacity: 0,
                    transform: 'translateY(10px)',
                }),
                animate('150ms ease-in', style({
                    opacity: 1,
                    transform: 'translateY(0)',
                })),
            ]),
            transition(':leave', [
                style({
                    opacity: 1,
                    transform: 'translateY(0)',
                }),
                animate('150ms ease-out', style({
                    opacity: 0,
                    transform: 'translateY(10px)',
                })),
            ]),
        ]),
    ],
})
export class DropdownMenuComponent {

    private _groups: GroupedMenuItems[] = [];

    // ----------[ Input ]----------

    @Input()
    set items(items: MenuItem[] | GroupedMenuItems[]) {
        if (items.length && 'group' in items[0]) {
            this._groups = items as GroupedMenuItems[];
        } else {
            this._groups = [{
                group: '',
                items: items as MenuItem[],
            }];
        }
    }

    @Input()
    active = false;

    @Input()
    align: 'left' | 'right' = 'left';

    constructor(private readonly router: Router) {
    }

    // ----------[ Event Handlers ]----------

    onClickMenuItem(item: MenuItem) {
        if ('execute' in item) {
            item.execute();
        } else {
            this.router.navigate(
                Array.isArray(item.href)
                    ? item.href
                    : [item.href],
            );
        }
    }

    // ----------[ Getters ]----------

    get groups(): GroupedMenuItems[] {
        return this._groups;
    }

    // ----------[ Helpers ]----------

    protected isLegacyIconName(icon: string | LucideIcon): icon is string {
        return typeof icon === 'string';
    }

}
